/**
 * Zustand store for shared client state related to API data fetching
 * (Requirement #3 of "Unified API client with RTK Query for all
 * frontend data fetching", extended by "Implement RTK Query hooks and
 * caching for all API endpoints" — Requirement #1: "Create Zustand
 * stores in src/stores/ to manage client-side state related to API
 * data").
 *
 * TanStack Query (`@/lib/api/apiClient`) owns *server* state (the actual
 * fetched data, its cache, retries, staleness). This store owns the
 * *client* state that shapes which server state gets fetched — filter/
 * sort/pagination UI state that (a) several components/hooks may need to
 * read or write, and (b) must survive re-renders without being
 * threaded through props — plus mutation-derived client state that has
 * no TanStack Query cache entry of its own (the active impersonation
 * session below: it's set by a `POST` mutation's result, not fetched by
 * a `GET` query). This is the same split already used by
 * `src/stores/authStore.ts` / `src/stores/trialStore.ts` (root `src/`
 * tree, Zustand for reactive client state) and `apps/web/src/stores/
 * impersonationStore.ts` (Context-based today; this is `apps/web`'s
 * first Zustand store).
 *
 * Concretely: `app/routes/admin/Subscriptions.tsx`'s filter/sort/
 * pagination controls (previously five separate `useState` calls) write
 * here; `useAdminSubscriptions()` (`@/lib/api/apiClient`) reads the
 * current filters to build its TanStack Query key, so changing a filter
 * automatically triggers a re-fetch via React Query's reactivity to a
 * changed query key — no manual `useEffect` refetch wiring needed.
 * `app/routes/admin/CustomersList.tsx` and `app/routes/admin/JobsList.tsx`
 * follow the identical pattern for `adminCustomersFilters` /
 * `adminJobsFilters`.
 *
 * `impersonationSession` note: intentionally a *new*, separate piece of
 * state on this store rather than a change to the existing Context-based
 * `stores/impersonationStore.ts` (a distinct, already-shipped feature
 * from Issue #281/#302 with its own `<ImpersonationProvider>` and tests
 * this issue does not otherwise touch) — `useStartImpersonation()` /
 * `useEndImpersonation()` (`@/lib/api/apiClient`) read/write this field
 * specifically, demonstrating this issue's "mutation succeeds -> Zustand
 * store updates" acceptance criterion end-to-end against a real,
 * DB-backed endpoint (`apps/workers/src/api/admin/impersonate.ts`).
 *
 * Related: GitHub issue #166 (admin subscriptions dashboard), #284
 * (admin console), #281/#302 (impersonation), "Unified API client with
 * RTK Query for all frontend data fetching", "Implement RTK Query hooks
 * and caching for all API endpoints"
 */

import { create } from 'zustand';
import type { AdminSubscriptionsQueryParams } from '@/lib/validation/subscriptions';
import type {
  AdminCustomersQueryParams,
  AdminJobsQueryParams,
  ImpersonateResponseDTO,
} from '@/lib/validation/admin';

/** Default admin subscriptions filter/sort/pagination state. */
const DEFAULT_ADMIN_SUBSCRIPTIONS_FILTERS: AdminSubscriptionsQueryParams = {
  limit: 20,
  offset: 0,
  sortBy: 'expires_at',
  sortOrder: 'asc',
  status: '',
  siloMode: '',
};

/** Default admin customers filter/pagination state
 * (`GET /api/admin/customers`). */
const DEFAULT_ADMIN_CUSTOMERS_FILTERS: AdminCustomersQueryParams = {
  limit: 20,
  offset: 0,
  search: '',
  plan: '',
  status: '',
};

/** Default admin jobs filter/sort/pagination state (`GET /api/admin/jobs`). */
const DEFAULT_ADMIN_JOBS_FILTERS: AdminJobsQueryParams = {
  limit: 20,
  offset: 0,
  search: '',
  sortBy: 'created_at',
  status: '',
};

/** Active admin impersonation session, set by `useStartImpersonation()`'s
 * mutation `onSuccess` and cleared by `useEndImpersonation()`'s. A subset
 * of `ImpersonateResponseDTO` (omits the JWT `token` itself — that's
 * handed to the impersonated-session's own auth flow, not held in this
 * client-state store). */
export type ActiveImpersonationSession = Omit<ImpersonateResponseDTO, 'token'>;

export interface ApiState {
  /**
   * Current filter/sort/pagination state for the admin subscriptions
   * dashboard (`GET /api/admin/subscriptions`).
   */
  adminSubscriptionsFilters: AdminSubscriptionsQueryParams;

  /** Shallow-merge a partial update into `adminSubscriptionsFilters`. */
  setAdminSubscriptionsFilters: (partial: Partial<AdminSubscriptionsQueryParams>) => void;

  /**
   * Toggle sort field/order the same way the previous local-`useState`
   * implementation did: clicking the currently-active sort field flips
   * its order; clicking a different field selects it ascending. Always
   * resets pagination to the first page (a changed sort order
   * invalidates the caller's notion of "page N").
   */
  toggleAdminSubscriptionsSort: (field: AdminSubscriptionsQueryParams['sortBy']) => void;

  /** Reset all admin subscriptions filters back to their defaults. */
  resetAdminSubscriptionsFilters: () => void;

  /**
   * Timestamp (ms since epoch) of the last successful
   * `GET /api/subscriptions/me` fetch, set by `useSubscriptionMe()`'s
   * `onSuccess`. Lets any component show "last synced" state without
   * re-deriving it from TanStack Query's internal cache metadata.
   */
  lastSubscriptionMeFetchedAt: number | null;
  setLastSubscriptionMeFetchedAt: (timestamp: number) => void;

  /**
   * Current filter/pagination state for the admin customers list
   * (`GET /api/admin/customers`, `app/routes/admin/CustomersList.tsx`).
   */
  adminCustomersFilters: AdminCustomersQueryParams;
  setAdminCustomersFilters: (partial: Partial<AdminCustomersQueryParams>) => void;
  resetAdminCustomersFilters: () => void;

  /**
   * Current filter/sort/pagination state for the admin jobs list
   * (`GET /api/admin/jobs`, `app/routes/admin/JobsList.tsx`).
   */
  adminJobsFilters: AdminJobsQueryParams;
  setAdminJobsFilters: (partial: Partial<AdminJobsQueryParams>) => void;
  resetAdminJobsFilters: () => void;

  /**
   * GitHub issue #563 ("[Metrics] Admin dashboard Opened/Bounced cards
   * are GLOBAL — ignore the customer filter"): the customer (`owner_id`
   * of an `AdminCustomerInfo`) currently selected in `AdminDashboard.tsx`'s
   * customer-scope dropdown. `''` means "platform-wide, unscoped" — same
   * convention as `adminJobsFilters.status`/`adminCustomersFilters.plan`.
   * `useAdminDashboard()` (`@/lib/api/apiClient`) reads this to build its
   * query key/`user_id` query param, so selecting a customer
   * automatically refetches every top-line card scoped to them.
   */
  adminDashboardCustomerId: string;
  setAdminDashboardCustomerId: (customerId: string) => void;

  /**
   * The currently-active admin impersonation session, if any. `null`
   * when no admin is impersonating a customer. Set by
   * `useStartImpersonation()`'s mutation `onSuccess`
   * (`@/lib/api/apiClient`); cleared by `useEndImpersonation()`'s.
   */
  impersonationSession: ActiveImpersonationSession | null;
  setImpersonationSession: (session: ActiveImpersonationSession) => void;
  clearImpersonationSession: () => void;
}

export const useApiStore = create<ApiState>((set, get) => ({
  adminSubscriptionsFilters: DEFAULT_ADMIN_SUBSCRIPTIONS_FILTERS,

  setAdminSubscriptionsFilters: (partial) =>
    set((state) => ({
      adminSubscriptionsFilters: { ...state.adminSubscriptionsFilters, ...partial },
    })),

  toggleAdminSubscriptionsSort: (field) => {
    const current = get().adminSubscriptionsFilters;
    const nextSortOrder =
      current.sortBy === field ? (current.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
    set({
      adminSubscriptionsFilters: {
        ...current,
        sortBy: field,
        sortOrder: nextSortOrder,
        offset: 0,
      },
    });
  },

  resetAdminSubscriptionsFilters: () =>
    set({ adminSubscriptionsFilters: DEFAULT_ADMIN_SUBSCRIPTIONS_FILTERS }),

  lastSubscriptionMeFetchedAt: null,
  setLastSubscriptionMeFetchedAt: (timestamp) => set({ lastSubscriptionMeFetchedAt: timestamp }),

  adminCustomersFilters: DEFAULT_ADMIN_CUSTOMERS_FILTERS,
  setAdminCustomersFilters: (partial) =>
    set((state) => ({
      adminCustomersFilters: { ...state.adminCustomersFilters, ...partial },
    })),
  resetAdminCustomersFilters: () => set({ adminCustomersFilters: DEFAULT_ADMIN_CUSTOMERS_FILTERS }),

  adminJobsFilters: DEFAULT_ADMIN_JOBS_FILTERS,
  setAdminJobsFilters: (partial) =>
    set((state) => ({
      adminJobsFilters: { ...state.adminJobsFilters, ...partial },
    })),
  resetAdminJobsFilters: () => set({ adminJobsFilters: DEFAULT_ADMIN_JOBS_FILTERS }),

  adminDashboardCustomerId: '',
  setAdminDashboardCustomerId: (customerId) => set({ adminDashboardCustomerId: customerId }),

  impersonationSession: null,
  setImpersonationSession: (session) => set({ impersonationSession: session }),
  clearImpersonationSession: () => set({ impersonationSession: null }),
}));
